import * as React from 'react';
import { useSelector } from 'react-redux';

import { removeCookie } from '../../../../browser/utils/js-cookie';
import { getTermValue } from '../../../../packages/JsonQuery';
import { getIsDealSaleBannerRotationEnabled } from '../../../selectors/dealSaleBanner';
import { getDealType } from '../../../selectors/getDealType';
import { getJsonQuery } from '../../../selectors/getJsonQuery';
import { getNewbuildingSimilarAdPath } from '../../../selectors/getNewbuildingSimilarAdPath';
import { getNewbuildingSimilarOffers } from '../../../selectors/getNewbuildingSimilarOffers';
import { getOfferType } from '../../../selectors/getOfferType';
import { selectIsMortgageBrokerBannerAvailable } from '../../../selectors/mortgageBrokerBanner';
import { selectIsNewbuildingConsultBannerAvailable } from '../../../selectors/newbuildingConsultBanner';
import { getIsNewbuildingBannerRotationEnabled } from '../../../selectors/newbuildingEntryBanner';
import { selectSuburbanBlackFridayBannerUrl } from '../../../selectors/suburbanBlackFriday';
import { EDealType } from '../../../types/dealType';
import { useApplicationContext } from '../../../utils/applicationContext';
import { FOfferType, isSuburban } from '../../../utils/category';
import { getNewbuildingEntryBannerInfo } from '../../../utils/getNewbuildingEntryBannerInfo';
import { NewbuildingConsultBannerContainer } from '../../components/NewbuildingConsultBanner';
import {
  NewbuildingEntryResaleBlock,
  trackNewbuildingEntryResaleBlockShow,
  trackNewbuildingEntryResaleBlockButtonClick,
  trackNewbuildingEntryResaleBlockItemClick,
} from '../../components/NewbuildingEntryResaleBlock';
import { SuburbanBlackFridayBannerContainerLoadable } from '../../components/SuburbanBlackFridayBanner';
import { SuburbanBuildersProjectsBanner } from '../../components/SuburbanBuildersProjectsBanner';
import { IIntegrateAdOptions } from '../../components/offers/helpers';
import { ObservableBannerServicePlace } from '../../components/service_places/banner/container';
import { DealSaleBannerContainer } from '../DealSaleBannerContainer';
import { MortgageBrokerBannerContainer } from '../MortgageBrokerBannerContainer';

export interface IBannerConatinerProps {
  options: IIntegrateAdOptions;
  position: number;
  isAgent?: boolean;
  locationId?: number;
  isCountrysideBuildersBannerAvailable?: boolean;
}

export function BannerContainer({
  options,
  position,
  isAgent,
  isCountrysideBuildersBannerAvailable,
}: IBannerConatinerProps) {
  const isNewbuildingBannerRotationEnabled = useSelector(getIsNewbuildingBannerRotationEnabled);
  const isDealSaleBannerRandomRotationEnabled = useSelector(getIsDealSaleBannerRotationEnabled);
  const isNewbuildingConsultBannerAvailable = useSelector(selectIsNewbuildingConsultBannerAvailable);
  const isMortgageBrokerBannerAvailable = useSelector(selectIsMortgageBrokerBannerAvailable);
  const dealType = useSelector(getDealType);
  const offerType = useSelector(getOfferType);
  const jsonQuery = useSelector(getJsonQuery);
  const nbSimilarOffers = useSelector(getNewbuildingSimilarOffers);
  const nbSimilarAdPath = useSelector(getNewbuildingSimilarAdPath);
  const { logger, config } = useApplicationContext();
  const isMortgageBannerEnabled = config.get<boolean>('mortgage.isSerpBannerEnabled');
  const isDealSaleBannerEnabled = config.get<boolean>('frontend-serp.DealSaleBanner.Enabled');
  const isFromDeveloperFilterEnabled = getTermValue('from_developer')(jsonQuery);
  const suburbanBlackFridayUrl = useSelector(selectSuburbanBlackFridayBannerUrl);

  React.useEffect(
    /* istanbul ignore next */ () => {
      if (!isCountrysideBuildersBannerAvailable) {
        removeCookie('buildersBannerShown');
      }
    },
    [isCountrysideBuildersBannerAvailable],
  );

  // Определяем, нужно ли показывать новостроечный баннер КпН в новостроечной и смешанной выдаче
  if (isNewbuildingBannerRotationEnabled && !isAgent && isNewbuildingConsultBannerAvailable) {
    return <NewbuildingConsultBannerContainer />;
  }

  // Определяем, нужно ли показывать блоки объявлений из новостроек в выдаче квартир вторички
  if (
    isNewbuildingBannerRotationEnabled &&
    nbSimilarOffers &&
    nbSimilarAdPath &&
    offerType === FOfferType.FlatOld &&
    dealType === EDealType.Sale
  ) {
    const { title, description } = getNewbuildingEntryBannerInfo('resale', config, logger);

    return (
      <NewbuildingEntryResaleBlock
        onShow={trackNewbuildingEntryResaleBlockShow(jsonQuery)}
        onButtonClick={trackNewbuildingEntryResaleBlockButtonClick(jsonQuery)}
        onItemClick={trackNewbuildingEntryResaleBlockItemClick(jsonQuery)}
        url={nbSimilarAdPath}
        title={title}
        description={description}
        items={nbSimilarOffers}
      />
    );
  }

  // Если в загородке в продаже акция "Черная пятница" активна, то показываем баннер
  if (isSuburban(offerType) && dealType === EDealType.Sale && suburbanBlackFridayUrl) {
    return <SuburbanBlackFridayBannerContainerLoadable href={suburbanBlackFridayUrl} />;
  }

  if ((offerType & FOfferType.Land) !== 0 && dealType === EDealType.Sale) {
    return <SuburbanBuildersProjectsBanner />;
  }

  // Если баннер "Циан.Сделка" включен, показываем его
  if (isDealSaleBannerRandomRotationEnabled && isDealSaleBannerEnabled && !isFromDeveloperFilterEnabled) {
    return <DealSaleBannerContainer />;
  }

  // Определяем, нужно ли показывать баннер Циан.Ипотека для профи
  if (isMortgageBrokerBannerAvailable) {
    return <MortgageBrokerBannerContainer />;
  }

  // Если ипотека выключена, то ничего не отображаем
  if (!isMortgageBannerEnabled) {
    return null;
  }

  // В остальных случаях показываем банер ипотеки
  return <ObservableBannerServicePlace options={options} position={position} />;
}
